<template>
  <div class="ui-color-filter" role="group" :aria-label="$t(ariaLabel)" :aria-disabled="String(disabled)">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      :class="{ 'is-active': value.includes(option.value) }"
      :style="{ '--ui-filter-color': option.color }"
      :aria-pressed="String(value.includes(option.value))"
      :disabled="isOptionDisabled(option)"
      @click="toggle(option.value)"
    >
      <i v-if="showDot"></i><span>{{ $t(option.label) }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'UiColorFilter',
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    ariaLabel: { type: String, default: '彩色选项筛选' },
    showDot: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false }
  },
  methods: {
    isOptionDisabled (option) {
      return this.disabled || Boolean(option && option.disabled)
    },
    toggle (optionValue) {
      const option = this.options.find(item => item.value === optionValue)
      if (!option || this.isOptionDisabled(option)) return
      const nextValue = this.value.includes(optionValue)
        ? this.value.filter(item => item !== optionValue)
        : [...this.value, optionValue]
      this.$emit('input', nextValue)
      this.$emit('change', nextValue)
    }
  }
}
</script>

<style lang="scss">
.ui-color-filter {
  height: var(--ui-control-height);
  min-height: var(--ui-control-height);
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: var(--ui-control-gap);
  box-sizing: border-box;
  line-height: 1;
  vertical-align: top;
}

.ui-color-filter > button {
  height: var(--ui-control-height);
  min-height: var(--ui-control-height);
  display: inline-flex;
  align-items: center;
  gap: var(--ui-space-xs);
  padding: 0 var(--ui-space-sm);
  color: var(--ui-text-primary);
  border: 1px solid var(--ui-border-color);
  border-radius: var(--ui-radius-small);
  background: transparent;
  box-sizing: border-box;
  font-size: var(--ui-font-size-body);
  font-weight: var(--ui-font-weight-medium);
  line-height: 1;
  vertical-align: top;
  cursor: pointer;
  transition: color var(--ui-duration-fast) ease,
    border-color var(--ui-duration-fast) ease,
    background-color var(--ui-duration-fast) ease;
}

.ui-color-filter > button.is-active {
  border-color: var(--ui-filter-color);
  background: var(--ui-bg-selected);
}

.ui-color-filter > button > i {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
  background: var(--ui-filter-color);
  opacity: .38;
  box-shadow: none;
  transition: opacity var(--ui-duration-fast) ease, box-shadow var(--ui-duration-fast) ease;
}

.ui-color-filter > button.is-active > i {
  opacity: 1;
  box-shadow: none;
}

.ui-color-filter > button:disabled {
  color: var(--ui-text-disabled);
  cursor: not-allowed;
  opacity: .5;
}

/* 仅有真实鼠标的设备显示 hover，避免触摸屏点击后残留悬停状态。 */
@media (hover: hover) and (pointer: fine) {
  .ui-color-filter > button:not(:disabled):not(.is-active):hover {
    color: var(--ui-text-primary);
    border-color: rgba(var(--ui-text-primary-rgb), .58);
    background: rgba(var(--ui-bg-elevated-rgb), .28);
  }

  .ui-color-filter > button:not(:disabled):not(.is-active):hover > i {
    opacity: .72;
    box-shadow: none;
  }
}

/* 按压反馈短促且不改变最终选中语义，PC 与触摸设备均可感知操作。 */
.ui-color-filter > button:not(:disabled):active {
  transform: scale(.97);
  filter: brightness(1.12);
  transition-duration: 60ms;
}

.ui-color-filter > button:focus:not(:focus-visible) {
  outline: none;
}

.ui-color-filter > button:not(:disabled):focus-visible {
  outline: 2px solid rgba(var(--ui-color-primary-rgb), .82);
  outline-offset: 2px;
}

@media (hover: none), (pointer: coarse) {
  .ui-color-filter > button:not(:disabled):hover:not(.is-active) {
    color: var(--ui-text-primary);
    border-color: var(--ui-border-color);
    background: transparent;
  }

  .ui-color-filter > button:not(:disabled):hover:not(.is-active) > i {
    opacity: .38;
    box-shadow: none;
  }
}
</style>
